import { fadeIn } from '@helpers/animate';
import { Icon, getSvgIcon } from '@helpers/icons';
import { withMotionMax } from '@hoc/withMotionMax';
import * as React from 'react';
import { useHoverIntent } from 'react-use-hoverintent';
import CtaBlock from '../CtaBlock/CtaBlock';
import GroupLink, { GroupLinkBlock, useGetGroupColourVars, } from '../GroupLink/GroupLink';
import { default as HS, default as HeaderStyles } from '../Header.styles';
import NavigationContactBlock from '../NavigationContactBlock/NavigationContactBlock';
import NavigationImage from '../NavigationImage/NavigationImage';
import S from './DesktopNavigation.styles';
// Add background behind the dropdown
const DesktopNavigation = (props) => {
    const [active, setActive] = React.useState(-1);
    const [immediateOpen, setImmediateOpen] = React.useState(true);
    React.useEffect(() => {
        if (active === -1) {
            setImmediateOpen(true);
        }
        else {
            setImmediateOpen(!(props.items[active].children &&
                props.items[active].children.length > 0));
        }
    }, [active]);
    if (!props.items || props.items.length === 0) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(S.Container, { onMouseLeave: () => setActive(-1), onBlur: (e) => {
                if (e.relatedTarget === null || !e.currentTarget.contains(e.relatedTarget)) {
                    setActive(-1);
                }
            } },
            props.items.map((item, i) => (React.createElement(NavigationLink, { key: `desktop-nav-${i}-${item.text}`, active: active, email: props.email, i: i, immediate: immediateOpen, item: item, setActive: setActive, socialLinks: props.socialLinks, telephone: props.telephone, translations: props.translations }))),
            active !== -1 && (React.createElement(NavigationDropdown, { active: active !== -1, email: props.email, immediate: immediateOpen, item: props.items[active], socialLinks: props.socialLinks, telephone: props.telephone, translations: props.translations }))),
        React.createElement(S.Bg, { variants: {
                initial: {
                    height: 0,
                    overflow: 'hidden',
                },
                hover: {
                    height: 'calc(100dvh - var(--headerHeight))',
                    transitionEnd: {
                        overflow: 'auto',
                    },
                },
            }, initial: "initial", animate: active !== -1 && props.items[active].children.length > 0
                ? 'hover'
                : 'initial', transition: {
                type: 'spring',
                stiffness: 200,
                damping: 33,
            } })));
};
const NavigationLink = ({ active, i, item, setActive }) => {
    const hasChildren = item.children && item.children.length > 0;
    const [hovered, anchorRef] = useHoverIntent({
        // These settings ensure the drop down doesn't appear until the user stops moving the mouse for 150ms
        interval: 250,
        sensitivity: 25,
    });
    React.useEffect(() => {
        if (hovered) {
            setActive(i);
        }
    }, [hovered]);
    return (React.createElement(S.TopLink, { ref: anchorRef, onMouseEnter: () => {
            if (active === -1) {
                setTimeout(() => setActive(i), 250);
            }
        } },
        React.createElement(S.TopLinkAnchor, { href: item.url, target: item.target, onFocus: () => setActive(i) },
            item.text,
            hasChildren && (React.createElement(S.TopLinkIcon, { variants: {
                    initial: { rotate: 0 },
                    hover: { rotate: 180 },
                }, initial: "initial", animate: active === i ? 'hover' : 'initial' }, getSvgIcon(Icon.Chevron))),
            active === i && (React.createElement(S.Underline, { layoutId: "desktop-navigation-underline", initial: false, transition: {
                    type: 'spring',
                    stiffness: 100,
                    damping: 12,
                } })))));
};
const NavigationDropdown = ({ active, email, immediate, item, socialLinks, telephone, translations, }) => {
    const hasChildren = item.children && item.children.length > 0;
    const singleChild = item.children && item.children.length === 1;
    const firstChild = item.children && item.children.length > 1
        ? item.children.find((c) => c.children && c.children.length > 0)
        : undefined;
    const [activeGroup, setActiveGroup] = React.useState(firstChild ? firstChild.id : '');
    const colours = useGetGroupColourVars(item);
    React.useEffect(() => {
        if (active) {
            setActiveGroup(firstChild ? firstChild.id : '');
        }
    }, [active, firstChild]);
    return hasChildren ? (React.createElement(S.Dropdown, { variants: {
            initial: {
                height: 0,
                overflow: 'hidden',
            },
            hover: {
                height: 'auto',
                transitionEnd: {
                    overflow: 'auto',
                },
            },
        }, initial: "initial", animate: active ? 'hover' : 'initial', transition: {
            delay: active && !immediate ? 0.66 : 0,
            type: 'spring',
            stiffness: 200,
            damping: 33,
        } },
        React.createElement(S.Inner, null,
            React.createElement(S.LeftColumn, null,
                singleChild ? (React.createElement("li", null,
                    React.createElement(GroupLinkBlock, { item: item.children?.[0], colours: colours?.[0] }))) : (React.createElement(React.Fragment, null,
                    React.createElement(S.LeftTitle, null, item.text),
                    item.children?.map((child, i) => (React.createElement(S.LeftItem, { key: `desktop-nav-child-${i}-${child.text}` },
                        React.createElement(NavigationGroupLink, { item: child, active: activeGroup === child.id, setActive: () => setActiveGroup(child.id), colours: colours?.[i] })))))),
                React.createElement(NavigationContactBlock, { email: email, socialLinks: socialLinks, telephone: telephone, translations: translations, as: "li" })),
            singleChild ? (React.createElement(S.RightColumn, null,
                React.createElement(S.LinkColumns, { style: {
                        '--arrowColour': (colours?.[0]).borderColour,
                    } },
                    item.children?.[0].children?.map((column, i) => (React.createElement(NavigationColumn, { key: `desktop-nav-link-${i}-${column.title}`, column: column }))),
                    item.children?.[0].image &&
                        item.children?.[0].children &&
                        item.children?.[0].children.length < 2 ? (React.createElement(NavigationImage, { ...item.children?.[0].image })) : null))) : (React.createElement(React.Fragment, null, item.children?.map((child, i) => (React.createElement(S.RightColumn, { key: `desktop-nav-column-${i}-${child.text}`, variants: fadeIn(), initial: "hidden", animate: activeGroup === child.id ? 'visible' : 'hidden' },
                React.createElement(GroupLinkBlock, { item: child, colours: colours?.[i] }),
                React.createElement(S.LinkColumns, { style: {
                        '--arrowColour': (colours?.[i]).borderColour,
                    } },
                    child.children?.map((column, j) => (React.createElement(NavigationColumn, { key: `desktop-nav-link-${i}-${child.text}-${j}`, column: column }))),
                    child.image && child.children && child.children.length < 3 ? (React.createElement(NavigationImage, { ...child.image })) : null)))))),
            item.ctaBlock && React.createElement(CtaBlock, { ...item.ctaBlock })),
        React.createElement(HS.Shadow, null))) : null;
};
const NavigationGroupLink = ({ item, active, setActive, colours, }) => {
    const hasChildren = item.children && item.children.length > 0;
    const [hovered, anchorRef] = useHoverIntent({
        // These settings ensure the drop down doesn't appear until the user stops moving the mouse for 150ms
        interval: 150,
        sensitivity: 100,
    });
    React.useEffect(() => {
        if (hovered && hasChildren) {
            setActive();
        }
    }, [hovered]);
    return (React.createElement(GroupLink, { ref: anchorRef, item: item, active: active, colours: colours, onClick: setActive }));
};
const NavigationColumn = ({ column, key }) => {
    return (React.createElement(S.LinkColumn, null,
        column.title &&
            (column.url ? (React.createElement(HeaderStyles.StyledLink, { href: column.url },
                React.createElement(S.LinkColumnTitle, null, column.title))) : (React.createElement(S.LinkColumnTitle, null, column.title))),
        column.items.map((link, k) => (React.createElement("li", { key: `${key}-${k}` },
            React.createElement(S.ColumnLink, { href: link.url, target: link.target },
                link.text,
                getSvgIcon(Icon.Arrow)))))));
};
export default withMotionMax(DesktopNavigation);
